/**
 * Quality-check badge for the wizard preview and the conversation simulator
 * (Marvin's QA, 2026-10-15). Every previewed message now carries the same
 * quality-gate verdict a live send would get, so the user sees whether it
 * would actually go out. Nothing in a preview is ever sent.
 */
import { AlertTriangle, CheckCircle2, Clock, ShieldX } from 'lucide-react'

/** Mirrors PreviewVerdict in workers/campaigns/src/services/preview-gate.ts. */
export type PreviewVerdict = {
  readonly action: 'auto_send' | 'send_with_review' | 'regenerate' | 'block' | 'unavailable'
  readonly score: number | null
  readonly outcome: 'send' | 'review' | 'rewrite' | 'blocked' | 'unavailable'
  readonly label: string
  readonly reason: string | null
}

const TONES: Record<PreviewVerdict['outcome'], string> = {
  send: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
  review: 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200',
  rewrite: 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200',
  blocked: 'border-destructive/30 bg-destructive/10 text-destructive',
  unavailable: 'border-border bg-muted text-muted-foreground',
}

/** Badge text with the score, e.g. "Would send · 8.5/10". Exported for tests. */
export function verdictText(v: PreviewVerdict): string {
  return v.score !== null ? `${v.label} · ${v.score}/10` : v.label
}

/** Colour classes for an outcome; unknown outcomes look neutral. Exported for tests. */
export function verdictTone(outcome: string): string {
  return TONES[outcome as PreviewVerdict['outcome']] ?? TONES.unavailable
}

export function PreviewVerdictBadge({ verdict }: { readonly verdict: PreviewVerdict }) {
  const Icon =
    verdict.outcome === 'send'
      ? CheckCircle2
      : verdict.outcome === 'blocked'
        ? ShieldX
        : verdict.outcome === 'unavailable'
          ? AlertTriangle
          : Clock
  return (
    <span
      className={`inline-flex max-w-full items-start gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-snug ${verdictTone(verdict.outcome)}`}
      title="Quality check: the same check a live send gets. Preview only, nothing is sent."
    >
      <Icon className="mt-px h-3 w-3 shrink-0" />
      <span className="break-words">{verdictText(verdict)}</span>
    </span>
  )
}
